import moment from "moment";
import { LoadState } from "../../../constants/enums";
import { getUniqueId } from "../../../helpers";
import { STANDARD_APP_DATE_FORMAT } from "../../../constants/constants";

export interface IAccountingInvoiceInformationState {
  list: IInvoiceDetails[];
  listLoading: LoadState;
  data: IInvoiceInformation;
  loading: LoadState;
  error: string | null;
  totalRecords: number;
  paymentHistory: IInvoicePaymentHistory;
  paymentHistoryLoading: LoadState;
  csioInvoicingList: ICSIOInvoicingInformation[];
  csioInvoicingListLoading: LoadState;
  transactionsList: IInvoiceTransactions[];
  transactionsLoading: LoadState;
  jeEntryInvoiceTransactionsList: IJeEntryInvoiceTransactions[];
  jeEntryInvoiceTransactionsLoading: LoadState;
  bulkInvoices: IBulkInvoices[];
  bulkInvoicesLoading: LoadState;
}

export interface IBulkInvoices {
  accn_invoice_transaction_items_id: string | null;
  invoice_date: string | null;
  invoice_due_date: string | null;
  broker_sub_code: string | null;
  broker_code: string | null;
  accn_transaction_id: string | null;
  transaction_type: string | null;
  gl_account_code: string | null;
  gl_sales_account_name: string | null;
  invoice_for: string | null;
  description: string | null;
  detail: string | null;
  amount: string | null;
  payment_terms: string | null;
  name: string | null;
  code: string | null;
  policy_id: string | null;
  policy_number: string | null;
  policy_type: string | null;
  policy_transaction_type: string | null;
  policy_status: string | null;
  transaction_date: string | null;
  transaction_effective_date: string | null;
  effective_date: string | null;
  expiry_date: string | null;
  default_tax_region: string | null;
  agency_or_direct_bill: string | null;
  agency_bill_commission: string | null;
  direct_bill_commission: string | null;
  agency_bill_receivables: string | null;
  direct_bill_receivables: string | null;
  premium_financed: string | null;
  financed_by: string | null;
  financed_fee: string | null;
  producer_one_id: string | null;
  producer_one_code: string | null;
  producer_two_id: string | null;
  producer_two_code: string | null;
  producer_three_id: string | null;
  producer_three_code: string | null;
  csr_code: string | null;
  branch: string | null;
  branch_code: string | null;
  insurer_code: string | null;
  tax_one_percentage: string | null;
  tax_one_amount: string | null;
  tax_two_percentage: string | null;
  tax_two_amount: string | null;
  commission_percentage: string | null;
  commission_amount: string | null;
  house_percentage: string | null;
  house_amount: string | null;
  producer_one_percentage: string | null;
  producer_one_amount: string | null;
  producer_two_percentage: string | null;
  producer_two_amount: string | null;
  producer_three_percentage: string | null;
  producer_three_amount: string | null;
  branch_percentage: string | null;
  branch_amount: string | null;
  total_amount: string | null;
  total_due_dollar: string | null;
  net_payable_insurer: string | null;
  current_amount_due: string | null;
  payment_paid: string | null;
  commission_paid: string | null;
  producer_paid: string | null;
  producer_paid_date: string | null;
  company_paid: string | null;
  company_paid_date: string | null;
  payment_paid_date: string | null;
  reconciled: string | null;
  payment_date: string | null;
  is_month_end: string | null;
  month_end_date: string | null;
  deleted_record: string | null;
  attachment: string | null;
  status: string | null;
}

export interface IBulkInvoiceRow {
  key: string;
  accn_invoice_transaction_items_id: string | null;
  accn_transaction_id: string | null;
  transaction_type: string | null;
  gl_account_code: string | null;
  gl_sales_account_name: string | null;
  description: string | null;
  detail: string | null;
  amount: string | null;
  policy_transaction_type: string | null;
  agency_bill_commission: string | null;
  direct_bill_commission: string | null;
  agency_bill_receivables: string | null;
  direct_bill_receivables: string | null;
  tax_one_percentage: string | null;
  tax_one_amount: string | null;
  tax_two_percentage: string | null;
  tax_two_amount: string | null;
  commission_percentage: string | null;
  commission_amount: string | null;
  house_percentage: string | null;
  house_amount: string | null;
  producer_one_percentage: string | null;
  producer_one_amount: string | null;
  producer_two_percentage: string | null;
  producer_two_amount: string | null;
  producer_three_percentage: string | null;
  producer_three_amount: string | null;
  branch_percentage: string | null;
  branch_amount: string | null;
  total_amount: string | null;
  total_due_dollar: string | null;
  net_payable_insurer: string | null;
  current_amount_due: string | null;
  payment_paid: string | null;
  commission_paid: string | null;
  producer_paid: string | null;
  producer_paid_date: string | null;
  company_paid: string | null;
  company_paid_date: string | null;
  payment_paid_date: string | null;
  reconciled: string | null;
  payment_date: string | null;
  is_month_end: string | null;
  month_end_date: string | null;
  deleted_record: string | null;
  attachment: string | null;
  status: string | null;
}

export interface ICSIOInvoicingInformation {
  csio_temp_accn_invoice_information_id: number;
  message_guid: string;
  temp_invoice_code: string | null;
  invoice_id: string | null;
  invoice_number: string | null;
  invoice_due_date: string;
  invoice_date: string;
  payment_terms: string;
  broker_code: string | null;
  broker_sub_code: string | null;
  accounting_information_id: string | null;
  gl_sales_account_name: string | null;
  name: string;
  postal_code: string;
  address: string;
  unit_or_suite: string | null;
  province_or_state: string;
  city: string;
  code: string;
  invoice_for: string;
  policy_id: string;
  policy_number: string;
  type_of_policy: string;
  policy_comission_code: string | null;
  is_mannual_invoice: number; // 0 or 1
  policy_transaction_type: string;
  policy_status: string | null;
  transaction_date: string | null;
  effective_date: string | null;
  transaction_effective_date: string | null;
  expiry_date: string | null;
  default_tax_region: string | null;
  agency_or_direct_bill: string | null;
  insurer_code: string;
  branch_code: string;
  branch: string | null;
  premium_financed: string | null;
  financed_by: string | null;
  invoice_status: string;
  invoice_details: IInvoiceDetails[];
  sub_total_dollar: string;
  net_payable_insurer: string;
  tax_one_total: string;
  tax_two_total: string;
  commission_total: string;
  invoice_notes: string | null;
  invoice_footer_information: string | null;
  total_due_dollar: string;
  financed_fee: string | null;
  policy_type: string;
  amount: string;
  amount_due: string;
  amount_paid: string | null;
  is_map: number; // 0 or 1
  error_message: string | null;
  comment: string | null;
  status: string;
  created_by_id: number | null;
  created_by_name: string | null;
  modified_by_id: number | null;
  modified_by_name: string | null;
  create_ts: string; // ISO timestamp
  insert_ts: string; // ISO timestamp
}

export interface ICSIOInvoicingDetail {
  message_guid: string | null;
  temp_invoice_code: string | null;
  type: string | null;
  amount: string | null;
  detail: string | null;
  description: string | null;
  house_amount: string | null;
  total_amount: string | null;
  net_payable_insurer: string | null;
  branch_amount: string | null;
  tax_one_percentage: string | null;
  tax_two_percentage: string | null;
  tax_one_amount: string | null;
  tax_two_amount: string | null;
  house_percentage: string | null;
  branch_percentage: string | null;
  transaction_type: string | null;
  producer_one_amount: string | null;
  producer_two_amount: string | null;
  commission_percentage: string | null;
  commission_amount: string | null;
  producer_three_amount: string | null;
  producer_one_percentage: string | null;
  producer_two_percentage: string | null;
  producer_three_percentage: string | null;
  ignore_insurer_code_validation: number;
  status: string;
}

export interface IInvoiceDetails {
  key: string;
  type: string | null;
  accn_invoice_transaction_items_id: number | null;
  transaction_type: string | null;
  detail: string | null;
  description: string | null;
  amount: string | null;
  tax_one_percentage: string | null;
  tax_one_amount: string | null;
  tax_two_percentage: string | null;
  tax_two_amount: string | null;
  commission_percentage: string | null;
  commission_amount: string | null;
  house_percentage: string | null;
  house_amount: string | null;
  producer_one_percentage: string | null;
  producer_one_amount: string | null;
  producer_two_percentage: string | null;
  producer_two_amount: string | null;
  producer_three_percentage: string | null;
  producer_three_amount: string | null;
  branch_percentage: string | null;
  branch_amount: string | null;
  net_payable_insurer: string | null;
  total_amount: string | null;
  commission_paid: string | null;
  payment_paid_date: string | null;
  reconciled: string | null;
  status: string;
  accn_transaction_id?: number;
  company_paid: string | null;
  company_paid_date: string | null;
  producer_paid: string | null;
  producer_paid_date: string | null;
}

export interface IInvoiceInformation {
  invoice_id: string | null;
  invoice_number: string | null;
  invoice_due_date: string | null;
  invoice_date: string | null;
  payment_terms: string | null;
  accounting_information_id: string | null;
  gl_sales_account_name: string | null;
  invoice_for: string;
  name: string | null;
  address: string | null;
  unit_or_suite: string | null;
  city: string | null;
  postal_code: string | null;
  code: string | null;
  policy_id: string | null;
  policy_number: string | null;
  type_of_policy: string;
  policy_transaction_type: string | null;
  policy_comission_code: string | null;
  policy_status: string | null;
  transaction_date: string | null;
  effective_date: string | null;
  transaction_effective_date: string | null;
  expiry_date: string | null;
  default_tax_region: string | null;
  agency_or_direct_bill: string | null;
  insurer_code: string | null;
  branch_code: string | null;
  branch: string | null;
  premium_financed: string | null;
  financed_by: string | null;
  invoice_details: IInvoiceDetails[];
  sub_total_dollar: string | null;
  net_payable_insurer: string;
  tax_one_total: string | null;
  tax_two_total: string | null;
  commission_total: string | null;
  is_mannual_invoice: boolean;
  invoice_notes: string | null;
  invoice_footer_information: string | null;
  total_due_dollar: string | null;
  invoice_status: string | null;
  status: string;
  policy_type: string | null;
  financed_fee: string | null;
  insert_ts?: string | null;
  amount_due: string | null;
  amount_paid: string | null;
  broker_code: string | null;
  broker_sub_code: string | null;
}

export interface IInvoicePaymentHistory {
  accn_invoice_information_id: number | null;
  invoice_id: number | null;
  invoice_number: string | null;
  invoice_date: string | null;
  invoice_due_date: string | null;
  invoice_status: string | null;
  payment_terms: string | null;
  accounting_information_id: string | null;
  gl_sales_account_name: string | null;
  gl_sales_account: string | null;
  gl_account_code: string | null;
  invoice_for: string | null;
  name: string | null;
  code: string | null;
  customer_id: string | null;
  policy_id: string | null;
  policy_number: string | null;
  policy_status: string | null;
  transaction_date: string | null;
  policy_transaction_type: string | null;
  effective_date: string | null;
  default_tax_region: string | null;
  agency_or_direct_bill: string | null;
  insurer_code: string | null;
  branch: string | null;
  branch_code: string | null;
  premium_financed: string | null;
  financed_by: string | null;
  sub_total_dollar: string | null;
  total_due_dollar: string | null;
  net_payable_insurer: string | null;
  invoice_notes: string | null;
  invoice_footer_information: string | null;
  type_of_policy: string | null;
  tax_one_total: string | null;
  tax_two_total: string | null;
  commission_total: string | null;
  is_mannual_invoice: string | null;
  policy_type: string | null;
  financed_fee: string | null;
  amount_due: string | null;
  amount_paid: string | null;
  status: string | null;
  created_by_id: string | null;
  modified_by_id: string | null;
  create_ts: string | null;
  insert_ts: string | null;
  payment_paid: string | null;
  remaining_amount_due: string | null;
  payment_details: IInvoiceHistoryPayment[];
  invoice_details: IInvoiceDetails[];
}

export interface IInvoiceHistoryPayment {
  accn_invoice_payment_transaction_items_id: string | null;
  invoice_id: string | null;
  invoice_number: string | null;
  accn_transaction_id: string | null;
  payment_number: string | null;
  entry_type: string | null;
  gl_accounts: string | null;
  invoice_payment_gl_account_name: string | null;
  date: string | null;
  batch_number: string | null;
  reciept_number: string | null;
  payment_for: string | null;
  customer_code: string | null;
  policy_number: string | null;
  payment_type: string | null;
  payment_currency: string | null;
  payment_amount: string | null;
  service_fee: string | null;
  payment_method: string | null;
  bank: string | null;
  net_payable: string | null;
  total_due_dollar: string | null;
  amount_due: string | null;
  payment_paid: string | null;
  payment_id: string | null;
  total_paid: string | null;
  is_partial_payment: string | null;
  status: string | null;
  created_by_id: string | null;
  modified_by_id: string | null;
  create_ts: string | null;
  insert_ts: string | null;
  actual_amount_due: string | null;
  invoice_due_date: string | null;
  transaction_type: string | null;
  gl_account_code: string | null;
  amount: string | null;
  payment_terms: string | null;
  customer_name: string | null;
  policy_type: string | null;
  policy_transaction_type: string | null;
  policy_status: string | null;
  transaction_date: string | null;
  effective_date: string | null;
  default_tax_region: string | null;
  agency_or_direct_bill: string | null;
  premium_financed: string | null;
  financed_by: string | null;
  financed_fee: string | null;
  producer_one_id: string | null;
  producer_one_code: string | null;
  producer_two_id: string | null;
  producer_two_code: string | null;
  producer_three_id: string | null;
  producer_three_code: string | null;
  csr_code: string | null;
  branch_code: string | null;
  insurer_code: string | null;
  tax_one_percentage: string | null;
  tax_one_amount: string | null;
  tax_two_percentage: string | null;
  tax_two_amount: string | null;
  commission_percentage: string | null;
  commission_amount: string | null;
  house_percentage: string | null;
  house_amount: string | null;
  producer_one_percentage: string | null;
  producer_one_amount: string | null;
  producer_two_percentage: string | null;
  producer_two_amount: string | null;
  producer_three_percentage: string | null;
  producer_three_amount: string | null;
  branch_percentage: string | null;
  branch_amount: string | null;
  total_amount: string | null;
  invoice_total_due_dollar: string | null;
}

export interface IInvoiceTransactions {
  accn_invoice_transaction_items_id: number | null;
  invoice_number: string | null;
  invoice_id: string | null;
  invoice_date: string | null;
  invoice_due_date: string | null;
  broker_code: string | null;
  broker_sub_code: string | null;
  accn_transaction_id: string | null;
  transaction_type: string | null;
  gl_account_code: string | null;
  gl_sales_account_name: string | null;
  invoice_for: string | null;
  description: string | null;
  detail: string | null;
  amount: string | null;
  payment_terms: string | null;
  name: string | null;
  code: string | null;
  policy_id: string | null;
  policy_number: string | null;
  policy_type: string | null;
  policy_transaction_type: string | null;
  policy_status: string | null;
  transaction_date: string | null;
  transaction_effective_date: string | null;
  effective_date: string | null;
  expiry_date: string | null;
  default_tax_region: string | null;
  agency_or_direct_bill: string | null;
  agency_bill_commission: string | null;
  direct_bill_commission: string | null;
  premium_financed: string | null;
  financed_by: string | null;
  financed_fee: string | null;
  producer_one_id: string | null;
  producer_one_code: string | null;
  producer_two_id: string | null;
  producer_two_code: string | null;
  producer_three_id: string | null;
  producer_three_code: string | null;
  csr_code: string | null;
  branch: string | null;
  branch_code: string | null;
  insurer_code: string | null;
  tax_one_percentage: string | null;
  tax_one_amount: string | null;
  tax_two_percentage: string | null;
  tax_two_amount: string | null;
  commission_percentage: string | null;
  commission_amount: string | null;
  house_percentage: string | null;
  house_amount: string | null;
  producer_one_percentage: string | null;
  producer_one_amount: string | null;
  producer_two_percentage: string | null;
  producer_two_amount: string | null;
  producer_three_percentage: string | null;
  producer_three_amount: string | null;
  branch_percentage: string | null;
  branch_amount: string | null;
  total_amount: string | null;
  total_due_dollar: string | null;
  net_payable_insurer: string | null;
  current_amount_due: string | null;
  payment_paid: string | null;
  commission_paid: string | null;
  producer_paid: string | null;
  producer_paid_date: string | null;
  company_paid: string | null;
  company_paid_date: string | null;
  payment_paid_date: string | null;
  reconciled: string | null;
  payment_date: string | null;
  is_month_end: string | null;
  month_end_date: string | null;
  deleted_record: string | null;
  status: string | null;
  created_by_id: string | null;
  created_by_name: string | null;
  modified_by_id: string | null;
  modified_by_name: string | null;
  create_ts: string | null;
  insert_ts: string | null;
  duplicate_indicator?: string;
  commission_adjustment?: boolean;
  attachment: string | null;
}

export interface IJeEntryInvoiceTransactions {
  accn_invoice_transaction_items_id: number | null;
  invoice_number: string | null;
  invoice_id: string | null;
  invoice_date: string | null;
  invoice_due_date: string | null;
  broker_code: string | null;
  broker_sub_code: string | null;
  accn_transaction_id: string | null;
  transaction_type: string | null;
  gl_account_code: string | null;
  gl_sales_account_name: string | null;
  invoice_for: string | null;
  description: string | null;
  detail: string | null;
  amount: string | null;
  payment_terms: string | null;
  name: string | null;
  code: string | null;
  policy_id: string | null;
  policy_number: string | null;
  policy_type: string | null;
  policy_transaction_type: string | null;
  policy_status: string | null;
  transaction_date: string | null;
  transaction_effective_date: string | null;
  effective_date: string | null;
  expiry_date: string | null;
  default_tax_region: string | null;
  agency_or_direct_bill: string | null;
  agency_bill_commission: string | null;
  direct_bill_commission: string | null;
  premium_financed: string | null;
  financed_by: string | null;
  financed_fee: string | null;
  producer_one_id: string | null;
  producer_one_code: string | null;
  producer_two_id: string | null;
  producer_two_code: string | null;
  producer_three_id: string | null;
  producer_three_code: string | null;
  csr_code: string | null;
  branch: string | null;
  branch_code: string | null;
  insurer_code: string | null;
  tax_one_percentage: string | null;
  tax_one_amount: string | null;
  tax_two_percentage: string | null;
  tax_two_amount: string | null;
  commission_percentage: string | null;
  commission_amount: string | null;
  house_percentage: string | null;
  house_amount: string | null;
  producer_one_percentage: string | null;
  producer_one_amount: string | null;
  producer_two_percentage: string | null;
  producer_two_amount: string | null;
  producer_three_percentage: string | null;
  producer_three_amount: string | null;
  branch_percentage: string | null;
  branch_amount: string | null;
  total_amount: string | null;
  total_due_dollar: string | null;
  net_payable_insurer: string | null;
  current_amount_due: string | null;
  payment_paid: string | null;
  commission_paid: string | null;
  producer_paid: string | null;
  producer_paid_date: string | null;
  company_paid: string | null;
  company_paid_date: string | null;
  payment_paid_date: string | null;
  reconciled: string | null;
  payment_date: string | null;
  is_month_end: string | null;
  month_end_date: string | null;
  deleted_record: string | null;
  status: string | null;
  created_by_id: string | null;
  created_by_name: string | null;
  modified_by_id: string | null;
  modified_by_name: string | null;
  create_ts: string | null;
  insert_ts: string | null;
  duplicate_indicator?: string;
  attachment: string | null;
}

export const initialInvoiceTransactionItems: IInvoiceTransactions = {
  accn_invoice_transaction_items_id: null,
  invoice_number: null,
  invoice_id: null,
  invoice_date: null,
  invoice_due_date: null,
  broker_code: null,
  broker_sub_code: null,
  accn_transaction_id: null,
  transaction_type: null,
  gl_account_code: null,
  gl_sales_account_name: null,
  invoice_for: null,
  description: null,
  detail: null,
  amount: null,
  payment_terms: null,
  name: null,
  code: null,
  policy_id: null,
  policy_number: null,
  policy_type: null,
  policy_transaction_type: null,
  policy_status: null,
  transaction_date: null,
  transaction_effective_date: null,
  effective_date: null,
  expiry_date: null,
  default_tax_region: null,
  agency_or_direct_bill: null,
  agency_bill_commission: null,
  direct_bill_commission: null,
  premium_financed: null,
  financed_by: null,
  financed_fee: null,
  producer_one_id: null,
  producer_one_code: null,
  producer_two_id: null,
  producer_two_code: null,
  producer_three_id: null,
  producer_three_code: null,
  csr_code: null,
  branch: null,
  branch_code: null,
  insurer_code: null,
  tax_one_percentage: null,
  tax_one_amount: null,
  tax_two_percentage: null,
  tax_two_amount: null,
  commission_percentage: null,
  commission_amount: null,
  house_percentage: null,
  house_amount: null,
  producer_one_percentage: null,
  producer_one_amount: null,
  producer_two_percentage: null,
  producer_two_amount: null,
  producer_three_percentage: null,
  producer_three_amount: null,
  branch_percentage: null,
  branch_amount: null,
  total_amount: null,
  total_due_dollar: null,
  net_payable_insurer: null,
  current_amount_due: null,
  payment_paid: null,
  commission_paid: null,
  producer_paid: null,
  producer_paid_date: null,
  company_paid: null,
  company_paid_date: null,
  payment_paid_date: null,
  reconciled: null,
  payment_date: null,
  is_month_end: null,
  month_end_date: null,
  deleted_record: null,
  status: null,
  created_by_id: null,
  created_by_name: null,
  modified_by_id: null,
  modified_by_name: null,
  attachment: null,
  create_ts: null,
  insert_ts: null,
};

export const initialJeEntryInvoiceTransactionItems: IJeEntryInvoiceTransactions =
  {
    accn_invoice_transaction_items_id: null,
    invoice_number: null,
    invoice_id: null,
    invoice_date: moment().format(STANDARD_APP_DATE_FORMAT),
    invoice_due_date: null,
    broker_code: null,
    broker_sub_code: null,
    accn_transaction_id: null,
    transaction_type: null,
    gl_account_code: "25800",
    gl_sales_account_name: null,
    invoice_for: "Customer",
    description: null,
    detail: null,
    amount: null,
    payment_terms: null,
    name: null,
    code: null,
    policy_id: null,
    policy_number: null,
    policy_type: null,
    policy_transaction_type: null,
    policy_status: null,
    transaction_date: moment().format(STANDARD_APP_DATE_FORMAT),
    transaction_effective_date: null,
    effective_date: null,
    expiry_date: null,
    default_tax_region: null,
    agency_or_direct_bill: null,
    agency_bill_commission: null,
    direct_bill_commission: null,
    premium_financed: null,
    financed_by: null,
    financed_fee: null,
    producer_one_id: null,
    producer_one_code: null,
    producer_two_id: null,
    producer_two_code: null,
    producer_three_id: null,
    producer_three_code: null,
    csr_code: null,
    branch: null,
    branch_code: null,
    insurer_code: null,
    tax_one_percentage: "0",
    tax_one_amount: "0",
    tax_two_percentage: "0",
    tax_two_amount: "0",
    commission_percentage: "0",
    commission_amount: "0",
    house_percentage: null,
    house_amount: "0",
    producer_one_percentage: "0",
    producer_one_amount: "0",
    producer_two_percentage: "0",
    producer_two_amount: "0",
    producer_three_percentage: "0",
    producer_three_amount: "0",
    branch_percentage: "0",
    branch_amount: "0",
    total_amount: null,
    total_due_dollar: null,
    net_payable_insurer: "0",
    current_amount_due: "0",
    payment_paid: null,
    commission_paid: null,
    producer_paid: null,
    producer_paid_date: null,
    company_paid: null,
    company_paid_date: null,
    payment_paid_date: null,
    reconciled: null,
    payment_date: null,
    is_month_end: null,
    month_end_date: null,
    deleted_record: null,
    status: "ACTIVE",
    created_by_id: null,
    created_by_name: null,
    modified_by_id: null,
    modified_by_name: null,
    create_ts: null,
    insert_ts: null,
    attachment: null
  };
export const bulkInvoices: IBulkInvoices = {
  accn_invoice_transaction_items_id: null,
  invoice_date: null,
  invoice_due_date: null,
  broker_sub_code: null,
  broker_code: null,
  accn_transaction_id: null,
  transaction_type: null,
  gl_account_code: null,
  gl_sales_account_name: null,
  invoice_for: null,
  description: null,
  detail: null,
  amount: null,
  payment_terms: null,
  name: null,
  code: null,
  policy_id: null,
  policy_number: null,
  policy_type: null,
  policy_transaction_type: null,
  policy_status: null,
  transaction_date: null,
  transaction_effective_date: null,
  effective_date: null,
  expiry_date: null,
  default_tax_region: null,
  agency_or_direct_bill: null,
  agency_bill_commission: null,
  direct_bill_commission: null,
  agency_bill_receivables: null,
  direct_bill_receivables: null,
  premium_financed: null,
  financed_by: null,
  financed_fee: null,
  producer_one_id: null,
  producer_one_code: null,
  producer_two_id: null,
  producer_two_code: null,
  producer_three_id: null,
  producer_three_code: null,
  csr_code: null,
  branch: null,
  branch_code: null,
  insurer_code: null,
  tax_one_percentage: null,
  tax_one_amount: null,
  tax_two_percentage: null,
  tax_two_amount: null,
  commission_percentage: null,
  commission_amount: null,
  house_percentage: null,
  house_amount: null,
  producer_one_percentage: null,
  producer_one_amount: null,
  producer_two_percentage: null,
  producer_two_amount: null,
  producer_three_percentage: null,
  producer_three_amount: null,
  branch_percentage: null,
  branch_amount: null,
  total_amount: null,
  total_due_dollar: null,
  net_payable_insurer: null,
  current_amount_due: null,
  payment_paid: null,
  commission_paid: null,
  producer_paid: null,
  producer_paid_date: null,
  company_paid: null,
  company_paid_date: null,
  payment_paid_date: null,
  reconciled: null,
  payment_date: null,
  is_month_end: null,
  month_end_date: null,
  deleted_record: null,
  attachment: null,
  status: "DRAFT",
};

export const initialAccountingInvoiceInformationState: IAccountingInvoiceInformationState =
  {
    list: [],
    listLoading: LoadState.NotLoaded,
    data: {
      invoice_id: null,
      invoice_number: null,
      invoice_due_date: moment().format(STANDARD_APP_DATE_FORMAT),
      invoice_date: moment().format(STANDARD_APP_DATE_FORMAT),
      payment_terms: "Due on Receipt",
      accounting_information_id: null,
      gl_sales_account_name: null,
      name: null,
      postal_code: null,
      address: null,
      unit_or_suite: null,
      city: null,
      code: null,
      invoice_for: "Customer",
      policy_id: null,
      policy_number: null,
      type_of_policy: "",
      policy_comission_code: null,
      is_mannual_invoice: false,
      policy_transaction_type: null,
      policy_status: null,
      transaction_date: moment().format(STANDARD_APP_DATE_FORMAT),
      effective_date: null,
      transaction_effective_date: moment().format(STANDARD_APP_DATE_FORMAT),
      expiry_date: null,
      default_tax_region: null,
      agency_or_direct_bill: null,
      insurer_code: null,
      branch_code: null,
      branch: null,
      premium_financed: null,
      financed_by: null,
      invoice_status: null,
      invoice_details: [
        {
          key: getUniqueId(),
          type: null,
          transaction_type: null,
          detail: null,
          description: null,
          amount: "0",
          tax_one_percentage: "0",
          tax_one_amount: "0",
          tax_two_percentage: "0",
          tax_two_amount: "0",
          commission_percentage: "0",
          commission_amount: "0",
          house_percentage: "0",
          house_amount: "0",
          producer_one_percentage: "0",
          producer_one_amount: "0",
          producer_two_percentage: "0",
          producer_two_amount: "0",
          producer_three_percentage: "0",
          producer_three_amount: "0",
          branch_percentage: "0",
          branch_amount: "0",
          net_payable_insurer: "0",
          total_amount: "0",
          commission_paid: "N",
          payment_paid_date: null,
          status: "ACTIVE",
          reconciled: "N",
          company_paid: "N",
          company_paid_date: null,
          producer_paid: "N",
          producer_paid_date: null,
          accn_invoice_transaction_items_id: null,
        },
      ],
      sub_total_dollar: "0",
      net_payable_insurer: "0",
      tax_one_total: "0",
      tax_two_total: "0",
      commission_total: "0",
      invoice_notes: null,
      invoice_footer_information: null,
      total_due_dollar: "0",
      status: "ACTIVE",
      financed_fee: null,
      policy_type: null,
      amount_due: null,
      amount_paid: null,
      broker_code: null,
      broker_sub_code: null,
    },
    loading: LoadState.NotLoaded,
    totalRecords: 0,
    error: null,
    paymentHistory: {
      invoice_status: null,
      accn_invoice_information_id: null,
      invoice_id: null,
      invoice_number: null,
      invoice_date: null,
      invoice_due_date: null,
      payment_terms: null,
      accounting_information_id: null,
      gl_sales_account_name: null,
      gl_sales_account: null,
      gl_account_code: null,
      invoice_for: null,
      name: null,
      code: null,
      customer_id: null,
      policy_id: null,
      policy_number: null,
      policy_status: null,
      transaction_date: null,
      policy_transaction_type: null,
      effective_date: null,
      default_tax_region: null,
      agency_or_direct_bill: null,
      insurer_code: null,
      branch: null,
      branch_code: null,
      premium_financed: null,
      financed_by: null,
      sub_total_dollar: null,
      total_due_dollar: null,
      net_payable_insurer: null,
      invoice_notes: null,
      invoice_footer_information: null,
      type_of_policy: null,
      tax_one_total: null,
      tax_two_total: null,
      commission_total: null,
      is_mannual_invoice: null,
      policy_type: null,
      financed_fee: null,
      amount_due: null,
      amount_paid: null,
      status: null,
      created_by_id: null,
      modified_by_id: null,
      create_ts: null,
      insert_ts: null,
      payment_paid: null,
      remaining_amount_due: null,
      payment_details: [],
      invoice_details: [],
    },
    paymentHistoryLoading: LoadState.NotLoaded,
    csioInvoicingList: [],
    csioInvoicingListLoading: LoadState.NotLoaded,
    transactionsList: [],
    transactionsLoading: LoadState.NotLoaded,
    bulkInvoices: [bulkInvoices],
    bulkInvoicesLoading: LoadState.NotLoaded,
    jeEntryInvoiceTransactionsList: [initialJeEntryInvoiceTransactionItems],
    jeEntryInvoiceTransactionsLoading: LoadState.NotLoaded,
  };
